import React from 'react';

import { getSeasonalMerchantsWithCustomTrackingId } from '../../../../sharedModules/getSeasonalMerchantsWithCustomTrackingId';
import { getWidgetInitialiser } from '../../../../sharedModules/getWidgetInitialiser';
import { loadCSS } from '../../../../utils/loadCSS';
import { WidgetResult } from '../../../hooks/useAnalytics';
import { GetInitializerPropsParams, InitialiserProps } from '../../../types/InitialiserProps';
import type { WidgetProps } from '../../../types/WidgetProps';
import { HawkWidgetContext } from '../../GenericComponents/HawkWidget/HawkWidgetContext';
import ReviewContracts from '../Contracts/ReviewContracts';
import ReviewRetail from '../Retail/ReviewRetail';
import ReviewSubscriptions from '../Subscriptions/ReviewSubscriptions';

import { getSortOptions } from './getSortOptions';

loadCSS('review');

type ReviewProps = {
  genericSharedComponents: WidgetProps['genericSharedComponents'];
  translate: WidgetProps['translate'];
  result: WidgetProps['result'];
  getDealData: WidgetProps['getDealData'];
  dealData: WidgetProps['dealData'];
  getWidgetResponse: WidgetProps['getWidgetResponse'];
  postForWidgetResponse: WidgetProps['postForWidgetResponse'];
  getAiredaleFeeds: WidgetProps['getAiredaleFeeds'];
  getModelSuggestions: WidgetProps['getModelSuggestions'];
  sendAnalytics: WidgetProps['sendAnalytics'];
};

export const Review: React.FC<ReviewProps> = (props) => {
  const {
    genericSharedComponents: { HawkWidgetAdvanced, Main },
    result,
  } = props;

  if (result === WidgetResult.MISSING) {
    return null;
  }

  return (
    <HawkWidgetAdvanced getSortOptions={getSortOptions} {...props}>
      <HawkWidgetContext.Consumer>
        {({ activeTab }): JSX.Element => {
          return (
            <Main className={activeTab.category} {...props}>
              {((): JSX.Element => {
                switch (activeTab.category) {
                  case 'subscriptions':
                    return <ReviewSubscriptions />;
                  case 'contracts':
                    return <ReviewContracts />;
                  default:
                    return <ReviewRetail />;
                }
              })()}
            </Main>
          );
        }}
      </HawkWidgetContext.Consumer>
    </HawkWidgetAdvanced>
  );
};

Review.getInitialiserProps = async ({
  renderProps,
  attributes,
  getWidgetResponse,
  getModelOffersResponse,
  getSeasonalResponse,
  getAiredaleFeeds,
  getModelSuggestions,
  postForWidgetResponse,
  editorial,
  defaultParams,
  origin,
  dataLinkMerchant,
}: GetInitializerPropsParams): InitialiserProps => {
  // Get the initialiser for the widget & render it when the initialiser resolves
  const { props, result } = await getWidgetInitialiser({
    editorial,
    defaultParams,
    origin,
    dataLinkMerchant,
    getWidgetResponse,
    getModelOffersResponse,
    getSeasonalResponse,
    getAiredaleFeeds,
    getModelSuggestions,
    postForWidgetResponse,
    attributes,
    ...renderProps,
  });

  const { data, area, defaultTab, seasonal, params } = props;
  if (seasonal === 'event' && defaultTab) {
    const dataEvent = data[defaultTab.value].event;
    if (dataEvent) {
      const offerType = data[defaultTab.value || '']['offer_type'];
      const battle = params[offerType || '']?.battle;

      dataEvent.merchants = await getSeasonalMerchantsWithCustomTrackingId(
        dataEvent,
        battle,
        renderProps.site,
        area,
        renderProps.articleType,
        props.articleCategory,
      );
    }
  }

  return { props, result };
};

export default Review;
